import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useBodyScrollLock } from "./shared";

// Scanner QR Code paket student.
// - Kamera baru diminta saat modal ini dibuka, dan semua track kamera
//   dimatikan saat modal ditutup / komponen dilepas.
// - Memakai BarcodeDetector bawaan browser kalau ada (Chrome Android, dll.),
//   selain itu jsQR yang baru di-load saat dibutuhkan (tidak masuk bundle
//   utama).
// - Hasil scan hanya dipakai sebagai token: data student selalu diambil dari
//   server (POST /student-package/scan).
const QR_PREFIX = "CEAPKG1.";
const SCAN_INTERVAL_MS = 200;
const MAX_FRAME_WIDTH = 640;

const cameraErrorMessage = (err) => {
  switch (err && err.name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Camera permission was denied. Allow camera access for this site in your browser settings, then press Start camera.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "No camera was found on this device.";
    case "NotReadableError":
    case "AbortError":
      return "The camera is being used by another app or tab. Close it, then press Start camera.";
    default:
      return "Unable to open the camera. Please try again.";
  }
};

const QrScannerModal = ({ onClose, onResolved, onUnauthorized }) => {
  useBodyScrollLock();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const busyRef = useRef(false);
  const decoderRef = useRef(null);
  const mountedRef = useRef(true);

  // idle | starting | scanning | checking | error
  const [phase, setPhase] = useState("idle");
  const [message, setMessage] = useState("");
  const [hint, setHint] = useState("");

  const stopCamera = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const getDecoder = async () => {
    if (decoderRef.current) return decoderRef.current;
    if ("BarcodeDetector" in window) {
      try {
        const formats = await window.BarcodeDetector.getSupportedFormats();
        if (formats.includes("qr_code")) {
          const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
          decoderRef.current = async (video) => {
            const codes = await detector.detect(video);
            return codes.length ? codes[0].rawValue : null;
          };
          return decoderRef.current;
        }
      } catch {
        // lanjut ke jsQR
      }
    }
    const { default: jsQR } = await import("jsqr");
    decoderRef.current = async (video) => {
      const scale = Math.min(1, MAX_FRAME_WIDTH / video.videoWidth);
      const w = Math.round(video.videoWidth * scale);
      const h = Math.round(video.videoHeight * scale);
      if (!w || !h) return null;
      if (!canvasRef.current) canvasRef.current = document.createElement("canvas");
      const canvas = canvasRef.current;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, w, h);
      const code = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: "dontInvert" });
      return code ? code.data : null;
    };
    return decoderRef.current;
  };

  const resolve = async (payload) => {
    busyRef.current = true;
    stopCamera();
    setPhase("checking");
    try {
      const res = await axios.post("/student-package/scan", { payload });
      if (mountedRef.current) onResolved(res.data);
    } catch (err) {
      if (!mountedRef.current) return;
      if (err?.response?.status === 401) return onUnauthorized();
      setPhase("error");
      setMessage(err?.response?.data?.msg || "Unable to check this QR Code. Please try again.");
    }
  };

  const tick = async () => {
    const video = videoRef.current;
    if (!streamRef.current || busyRef.current || !video) return;
    try {
      if (video.readyState >= 2 && video.videoWidth) {
        const decode = await getDecoder();
        const value = await decode(video);
        if (value && mountedRef.current && !busyRef.current) {
          if (String(value).startsWith(QR_PREFIX)) return resolve(String(value));
          setHint("This is not a CEA student package QR Code.");
        }
      }
    } catch {
      // Frame yang gagal dibaca dilewati saja.
    }
    if (streamRef.current && !busyRef.current) timerRef.current = setTimeout(tick, SCAN_INTERVAL_MS);
  };

  const startCamera = async () => {
    stopCamera();
    busyRef.current = false;
    setHint("");
    setMessage("");
    if (!window.isSecureContext) {
      setPhase("error");
      setMessage("The camera only works on a secure (HTTPS) page or on localhost.");
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setPhase("error");
      setMessage("This browser cannot open the camera. Try the latest Chrome, Safari or Edge.");
      return;
    }
    setPhase("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      if (!mountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play().catch(() => {});
      setPhase("scanning");
      timerRef.current = setTimeout(tick, SCAN_INTERVAL_MS);
    } catch (err) {
      if (!mountedRef.current) return;
      setPhase("error");
      setMessage(cameraErrorMessage(err));
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    startCamera();
    return () => {
      mountedRef.current = false;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cameraOn = phase === "starting" || phase === "scanning";

  return createPortal(
    <div className="adm-modal-backdrop" onClick={onClose}>
      <div
        className="adm-modal spk-scanner"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spk-scanner-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="adm-modal-header">
          <div>
            <h5 id="spk-scanner-title" className="adm-card-title" style={{ fontSize: "17px" }}>
              Scan Student Package
            </h5>
            <p className="adm-card-subtitle">Point the camera at the QR Code in the student's portal Profile.</p>
          </div>
          <button type="button" className="adm-modal-close" aria-label="Close scanner" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="adm-modal-body">
          <div className={`spk-camera${cameraOn ? " is-on" : ""}`}>
            <video ref={videoRef} playsInline muted autoPlay aria-label="Camera preview" />
            {phase === "scanning" && <div className="spk-camera-frame" aria-hidden="true" />}
            {phase === "starting" && <p className="spk-camera-note">Opening the camera…</p>}
            {phase === "checking" && (
              <p className="spk-camera-note">
                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Checking QR Code…
              </p>
            )}
            {phase === "idle" && <p className="spk-camera-note">Camera is off.</p>}
            {phase === "error" && (
              <p className="spk-camera-note is-error" role="alert">
                <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
                {message}
              </p>
            )}
          </div>
          <p className="spk-scan-hint" role="status" aria-live="polite">
            {phase === "scanning" ? hint || "Scanning… hold the QR Code steady inside the frame." : ""}
          </p>
        </div>

        <div className="adm-modal-footer">
          {cameraOn ? (
            <button
              type="button"
              className="adm-btn adm-btn--secondary"
              onClick={() => {
                stopCamera();
                setPhase("idle");
              }}
            >
              <i className="bi bi-stop-circle"></i> Stop camera
            </button>
          ) : (
            <button type="button" className="adm-btn adm-btn--primary" onClick={startCamera} disabled={phase === "checking"}>
              <i className="bi bi-camera"></i> {phase === "error" ? "Scan again" : "Start camera"}
            </button>
          )}
          <button type="button" className="adm-btn adm-btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default QrScannerModal;
